import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BookSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paymentIntentId = params.get("payment_intent");
  const redirectStatus = params.get("redirect_status");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    // Path A — embedded PaymentIntent flow (preferred)
    if (paymentIntentId) {
      if (redirectStatus === "succeeded") {
        setStatus("paid");
        return;
      }
      const MAX = 8;
      const INTERVAL = 2000;
      let cancelled = false;
      const poll = async (n) => {
        if (cancelled) return;
        try {
          const res = await axios.get(
            `${API}/api/payment-intent/status/${paymentIntentId}`
          );
          if (res.data?.payment_status === "paid") {
            setStatus("paid");
            return;
          }
          if (n >= MAX) {
            setStatus("error");
            return;
          }
          setAttempts(n + 1);
          setTimeout(() => poll(n + 1), INTERVAL);
        } catch {
          if (n >= MAX) {
            setStatus("error");
            return;
          }
          setAttempts(n + 1);
          setTimeout(() => poll(n + 1), INTERVAL);
        }
      };
      poll(0);
      return () => {
        cancelled = true;
      };
    }

    // Path B — legacy Stripe Checkout Session flow
    if (!sessionId) {
      setStatus("error");
      return;
    }
    const MAX = 8;
    const INTERVAL = 2500;

    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/api/checkout/status/${sessionId}`);
        const ps = res.data?.payment_status;
        const st = res.data?.status;
        if (ps === "paid") {
          setStatus("paid");
          return;
        }
        if (st === "expired") {
          setStatus("expired");
          return;
        }
        if (n >= MAX) {
          setStatus("error");
          return;
        }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), INTERVAL);
      } catch {
        if (n >= MAX) {
          setStatus("error");
          return;
        }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), INTERVAL);
      }
    };
    poll(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId, paymentIntentId, redirectStatus]);

  return (
    <main
      data-testid="book-success-page"
      className="relative bg-[#0A0A0C] text-[#F5F5F5] min-h-screen overflow-x-hidden"
    >
      <Nav />

      <section className="relative bg-black min-h-screen flex items-center">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[80vw] h-[600px] opacity-50"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(220,38,38,0.18) 0%, rgba(127,29,29,0.04) 40%, transparent 70%)",
          }}
        />

        <div className="relative mx-auto max-w-[680px] px-6 md:px-10 py-32 text-center">
          {status === "checking" && (
            <div data-testid="status-checking">
              <Loader2 className="h-10 w-10 mx-auto text-[#FCA5A5] animate-spin" />
              <h1 className="mt-8 font-display font-black tracking-tighter text-4xl md:text-6xl text-white leading-[0.95]">
                Confirming your{" "}
                <span
                  className="font-script italic font-normal"
                  style={{
                    background:
                      "linear-gradient(180deg, #ffffff 0%, #FCA5A5 30%, #DC2626 70%, #7F1D1D 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "transparent",
                    paddingBottom: "0.18em",
                    paddingRight: "0.05em",
                    display: "inline-block",
                    lineHeight: 1,
                  }}
                >
                  payment…
                </span>
              </h1>
              <p className="mt-6 text-white/60 text-sm font-mono uppercase tracking-[0.28em]">
                Attempt {attempts + 1} of 8
              </p>
            </div>
          )}

          {status === "paid" && (
            <div data-testid="status-paid">
              <CheckCircle2 className="h-12 w-12 mx-auto text-[#DC2626]" />
              <h1 className="mt-8 font-display font-black tracking-tighter text-4xl md:text-6xl text-white leading-[0.95]">
                Thank you,{" "}
                <span
                  className="font-script italic font-normal whitespace-nowrap"
                  style={{
                    background:
                      "linear-gradient(180deg, #ffffff 0%, #FCA5A5 30%, #DC2626 70%, #7F1D1D 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "transparent",
                    paddingBottom: "0.18em",
                    paddingRight: "0.05em",
                    display: "inline-block",
                    lineHeight: 1,
                  }}
                >
                  truly.
                </span>
              </h1>
              <p className="mt-6 max-w-md mx-auto text-base md:text-lg text-white/70 leading-relaxed">
                Your copy of <em className="text-white not-italic font-medium">The Butterfly Effect</em>{" "}
                is on its way. Cameron will follow up with shipping details by
                email shortly.
              </p>
              <a
                href="/"
                data-testid="success-home-link"
                className="mt-10 cta-clay-blue group inline-flex items-center gap-2 h-12 px-7 rounded-full text-xs font-mono font-semibold uppercase tracking-[0.2em] text-white"
              >
                Back home
              </a>
            </div>
          )}

          {status === "expired" && (
            <div data-testid="status-expired">
              <AlertCircle className="h-12 w-12 mx-auto text-white/60" />
              <h1 className="mt-8 font-display font-black tracking-tighter text-4xl md:text-5xl text-white">
                Session expired.
              </h1>
              <p className="mt-5 text-white/65">
                The checkout timed out. No charge was made.
              </p>
              <a
                href="/publications"
                className="mt-10 cta-clay-blue group inline-flex items-center gap-2 h-12 px-7 rounded-full text-xs font-mono font-semibold uppercase tracking-[0.2em] text-white"
              >
                Try again
              </a>
            </div>
          )}

          {status === "error" && (
            <div data-testid="status-error">
              <AlertCircle className="h-12 w-12 mx-auto text-white/60" />
              <h1 className="mt-8 font-display font-black tracking-tighter text-4xl md:text-5xl text-white">
                Still processing…
              </h1>
              <p className="mt-5 max-w-md mx-auto text-white/65 leading-relaxed">
                We couldn't confirm the payment status here. If the charge went
                through, you'll get an emailed receipt shortly. Otherwise no
                charge was made.
              </p>
              <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
                <a
                  href="mailto:inquiries@cameronbergsma.com"
                  className="group inline-flex items-center gap-2 h-12 px-6 rounded-full border border-white/20 text-xs font-mono uppercase tracking-[0.2em] text-white/80 hover:text-white hover:border-white/40 transition-colors"
                >
                  Email support
                </a>
                <a
                  href="/publications"
                  className="cta-clay-blue group inline-flex items-center gap-2 h-12 px-7 rounded-full text-xs font-mono font-semibold uppercase tracking-[0.2em] text-white"
                >
                  Back to book
                </a>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
